import {AbbreviatedLegacyClass} from "@/shared/legacyTypes/fetchAllClassesForCampusTypes";
import {DayOfWeek} from "@/shared/legacyTypes/fetchClassesTypes";
import {DAY_OF_WEEK_MAP} from "@/shared/legacyTypes/fetchClassesTypes";
import {
	FilterOptions,
	FilterSelectOptions,
	SortedAcademyClasses,
} from "./classScheduleTypes";
import {Class} from "@/lib/legacyApi/fetchClassesForCourseByCampusId";
import pluralize from "pluralize";
import {Subject} from "./Subject";

const dayRankMap = {
	[DAY_OF_WEEK_MAP.MONDAY]: 0,
	[DAY_OF_WEEK_MAP.TUESDAY]: 1,
	[DAY_OF_WEEK_MAP.WEDNESDAY]: 2,
	[DAY_OF_WEEK_MAP.THURSDAY]: 3,
	[DAY_OF_WEEK_MAP.FRIDAY]: 4,
	[DAY_OF_WEEK_MAP.SATURDAY]: 5,
	[DAY_OF_WEEK_MAP.SUNDAY]: 6,
};

/**
 * Checks if the seasonId is an academic year season
 *
 * TODO: This is the same check that we use in legacy, but...idk.  We'll probably
 *       hopefully maybe want to do something else eventually.  Or at least a more
 *       rigid check considering they always end with -2, but like.  Current me
 *       thinks it's more important that they match.
 * @param seasonId - The seasonId to check
 * @returns True if the seasonId string contains "-2", false otherwise
 */
export function isAcademicYearSeason(seasonId: string): boolean {
	return /-2/.test(seasonId);
}

/**
 * Checks if the seasonId is a summer season
 *
 * TODO: See isAcademicYearSeason todo
 * @param seasonId - The seasonId to check
 * @returns True if the seasonId string contains "-1", false otherwise
 */
export function isSummerSeason(seasonId: string): boolean {
	return /-1/.test(seasonId);
}

/**
 * Gets all the classes for a given season.
 *
 * @param academyClasses - The complete class list
 * @param seasonId - The season id to get the classes for
 * @returns The classes assigned to the given season
 */
export function getSeasonClasses(
	academyClasses: AbbreviatedLegacyClass[],
	seasonId: string
): AbbreviatedLegacyClass[] {
	return academyClasses.filter((classItem) => classItem.seasonId === seasonId);
}

/**
 * General utility function to identify a time slot for a class item.
 * This could be anything as long as we have some kind of convention.
 * For now it's ${timeStartAt}-${timeEndAt}
 *
 * @param classItem - The legacy class item to create a time slot id from
 * @returns The time slot id
 */
export function makeTimeSlotId(classItem: AbbreviatedLegacyClass): string {
	return `${classItem.timeStartAt}-${classItem.timeEndAt}`;
}

/**
 * Derives the human readable time slot string from the time slot id.  Right
 * now this removes leading zeros from the start and end times and results in something
 * like "10:00 AM - 11:00 AM"
 *
 * @param id - The time slot id to create a time slot string from
 * @returns The time slot string
 */
export function makeTimeSlotStringFromId(id: string): string {
	const [start, end] = id.split("-");
	return `${start.replace(/^0+/, "")} - ${end.replace(/^0+/, "")}`.toUpperCase();
}

/**
 * Derives a date range id for the given start and end dates.
 *
 * @param startDate - The start date
 * @param endDate - The end date
 * @returns The date range id
 */
export function makeDateRangeId(startDate: Date, endDate: Date): string {
	return `${startDate.getUTCMonth() + 1}/${startDate.getUTCDate()}/${startDate.getUTCFullYear()}-${endDate.getUTCMonth() + 1}/${endDate.getUTCDate()}/${endDate.getUTCFullYear()}`;
}

/**
 * Derives the human readable date range string from the date range id.  For now
 * this just adds spaces.  But I'm leaving this function here just in case we
 * want to change it.
 *
 * @param id - The date range id to create a date range string from
 * @returns The date range string
 */
export function makeDateStringFromId(id: string): string {
	const [start, end] = id.split("-");
	const startDate = new Date(start);
	const endDate = new Date(end);
	return `${startDate.toLocaleDateString("en-US", {month: "long", day: "numeric"})} - ${endDate.toLocaleDateString("en-US", {month: "long", day: "numeric"})}`;
}

/**
 * Derives a bucket id for the days on which the class occurs.
 * Again, this can be anything so long as we establish a convention.
 * This function is here for that.
 *
 * @param classItem - The class item to derive an id based on the days of week
 *                    on which it occurs for.
 * @returns The id
 */
export function makeClassDaysId(
	classItem: AbbreviatedLegacyClass | Class
): string {
	return classItem.dow.sort(compareDaysOfWeek).join("-");
}

/**
 * Derives the human readable class days string from the class days id.
 * If the days are consecutive, returns FirstDay-LastDay.
 * Otherwise, returns all days as a comma separated list.
 *
 * @param id - The class days id to create a human readable string from
 * @returns The human readable class days string
 */
export function makeClassDaysStringFromId(id: string): string {
	const days = id.split("-").sort(compareDaysOfWeek);

	if (days.length < 2) {
		return pluralize(days[0]);
	}

	// Check if days are consecutive
	const isConsecutive = days.every((day, i) => {
		if (i === 0) {
			return true;
		}
		const prevDayRank = dayRankMap[days[i - 1] as DayOfWeek];
		const currDayRank = dayRankMap[day as DayOfWeek];
		return currDayRank === prevDayRank + 1;
	});

	// if consecutive, return FirstDay-LastDay
	if (isConsecutive && days.length > 1) {
		return `${days[0].replace("day", "")}-${days[days.length - 1].replace("day", "")}`;
	}

	// otherwise, return all days as a comma separated list
	return days.map((d) => pluralize(d)).join(", ");
}

/**
 * Sorts classes by season > time slot > day of week
 * @param classes - The classes to sort
 * @returns The sorted classes by season > time slot > day of week
 */
export function sortAcademyClasses(classes: AbbreviatedLegacyClass[]): {
	[seasonId: string]: SortedAcademyClasses;
} {
	const alphabetisedClassesBySubject = [...classes].sort((a, b) => {
		const subjectRank = {
			[Subject.MATH as string]: 1,
			[Subject.LANGUAGE_ARTS as string]: 2,
			[Subject.SCIENCE as string]: 3,
			unknown: 4,
		};
		return (
			subjectRank[a.subject ?? "unknown"] -
				subjectRank[b.subject ?? "unknown"] || a.name.localeCompare(b.name)
		);
	});
	return alphabetisedClassesBySubject.reduce(
		(
			acc: {[seasonId: string]: SortedAcademyClasses},
			classItem: AbbreviatedLegacyClass
		) => {
			const {seasonId, dateStartAt, dateEndAt} = classItem;
			const timeSlot = makeTimeSlotId(classItem);
			const classDays = makeClassDaysId(classItem);

			if (!acc[seasonId]) {
				acc[seasonId] = {};
			}

			const dateRange = makeDateRangeId(
				new Date(dateStartAt),
				new Date(dateEndAt)
			);

			if (!acc[seasonId][dateRange]) {
				acc[seasonId][dateRange] = {};
			}
			if (!acc[seasonId][dateRange][classDays]) {
				acc[seasonId][dateRange][classDays] = {};
			}
			if (!acc[seasonId][dateRange][classDays][timeSlot]) {
				acc[seasonId][dateRange][classDays][timeSlot] = [];
			}

			acc[seasonId][dateRange][classDays][timeSlot].push(classItem);

			return acc;
		},
		{} as {[seasonId: string]: SortedAcademyClasses}
	);
}

/**
 * Derives the date ranges by season for a given set of academy classes.
 *
 * Classes are considered to be in the same date range if they start within
 * a week of each other .  The end date of a given range is given by the
 * latest end date of any class that starts within the given week.
 *
 * The start date for each range is essentially the earliest start date of a
 * class that does not fall into an existing date range, as we can't make any
 * assumptions on the day of week any range starts on (e.g. campuses define different
 * days of week to start their academic year).
 *
 * @param academyClasses - The academy classes to derive the date ranges from
 * @returns The date ranges of the academy classes by season
 */
export function deriveDateRangesBySeason(
	academyClasses: AbbreviatedLegacyClass[]
): Record<string, {startDate: Date; endDate: Date}[]> {
	const exactDatesBySeason: Record<string, {startDate: Date; endDate: Date}[]> =
		{};

	academyClasses.forEach((classItem) => {
		if (!exactDatesBySeason[classItem.seasonId]) {
			exactDatesBySeason[classItem.seasonId] = [];
		}

		const hasMatch = exactDatesBySeason[classItem.seasonId].find((date) => {
			return (
				date.startDate.getTime() ===
					new Date(classItem.dateStartAt).getTime() &&
				date.endDate.getTime() === new Date(classItem.dateEndAt).getTime()
			);
		});

		if (!hasMatch) {
			exactDatesBySeason[classItem.seasonId].push({
				startDate: new Date(classItem.dateStartAt),
				endDate: new Date(classItem.dateEndAt),
			});
		}
	});

	const weekBucketsBySeason: Record<
		string,
		{startDate: Date; endDate: Date}[]
	> = {};

	Object.keys(exactDatesBySeason).forEach((seasonId) => {
		const dates = exactDatesBySeason[seasonId].sort(
			(a, b) => a.startDate.getTime() - b.startDate.getTime()
		);
		if (!dates.length) {
			return;
		}

		const weekBuckets: {startDate: Date; endDate: Date}[] = [dates[0]];

		let currentBucketStart = dates[0].startDate;
		let currentBucketEnd = dates[0].endDate;

		for (const {startDate, endDate} of dates) {
			// if startDate is within a week of currentBucketStart, we put it in the same bucket
			if (
				currentBucketStart.getTime() <= startDate.getTime() &&
				startDate.getTime() <
					currentBucketStart.getTime() + 7 * 24 * 60 * 60 * 1000
			) {
				if (endDate.getTime() > currentBucketEnd.getTime()) {
					// for tracking purposes, we want to maintain that the end date is the last
					// day any class that starts within the same time frame takes place.
					// NOTE: this may cause issues down the line, but for now we don't do
					//       a "within the week of" logic, presumably because some holidays/scheduling
					//       hiccups lead to classes ending later than expected.
					weekBuckets[weekBuckets.length - 1].endDate = endDate;
				}
				continue;
			}

			// otherwise, add the current bucket to the weekBuckets and start a new bucket
			weekBuckets.push({startDate, endDate});
			currentBucketStart = startDate;
			currentBucketEnd = endDate;
		}

		weekBucketsBySeason[seasonId] = weekBuckets;
	});

	return weekBucketsBySeason;
}

/**
 * Compares two time slots to determine their order.  We order by start time.
 *
 * Do NOT use this to get the difference between two time slots.  I have no idea
 * how that would work, but it's not this.
 *
 * @param a - The first time slot to compare
 * @param b - The second time slot to compare
 * @returns The difference between the two time slots when each start time is converted
 *          to a 24 hour integer (e.g. "10:00 AM" -> 1000, "11:00 AM" -> 1100.)
 */
export function compareTimeSlots(a: string, b: string): number {
	const [aStart] = a.split("-");
	const [bStart] = b.split("-");

	const convertTo24HourInt = (time: string) => {
		const [timePart, period] = time.split(" ");
		const [hours, minutes] = timePart.split(":");
		let hour = parseInt(hours);
		if (period.toLowerCase() === "pm" && hour !== 12) {
			hour += 12;
		}
		if (period.toLowerCase() === "am" && hour === 12) {
			hour = 0;
		}
		return hour * 100 + parseInt(minutes);
	};

	return convertTo24HourInt(aStart) - convertTo24HourInt(bStart);
}

/**
 * Compares two days of week to determine their order.  For our purposes, Monday
 * is the first day of the week.
 *
 * @param a - The first day of week to compare
 * @param b - The second day of week to compare
 * @returns The difference between the two days of week where Monday is 0 and Sunday is 6
 */
export function compareDaysOfWeek(
	a: string | DayOfWeek,
	b: string | DayOfWeek
): number {
	return dayRankMap[a as DayOfWeek] - dayRankMap[b as DayOfWeek];
}

/**
 * Compares two class days ids to determine their order.  We only care about
 * the first day of the week the class occurs on. (e.g. if the class occurs
 * on Monday and Tuesday, we only care about Monday.)
 *
 * @param a - The first class days id to compare
 * @param b - The second class days id to compare
 * @returns The difference between the first day of the week for the two class days ids
 */
export function compareClassDays(a: string, b: string): number {
	const [firstDayOfA] = a.split("-");
	const [firstDayOfB] = b.split("-");

	return compareDaysOfWeek(firstDayOfA, firstDayOfB);
}

/**
 * Compares two date ranges to determine their order.  We order by start date.
 *
 * @param a - The first date range to compare
 * @param b - The second date range to compare
 * @returns The difference between the two date ranges when each start date is converted
 *          to a date object and compared.
 */
export function compareDates(a: string, b: string): number {
	const [aStart] = a.split("-");
	const [bStart] = b.split("-");

	const aStartDate = new Date(aStart);
	const bStartDate = new Date(bStart);

	return aStartDate.getTime() - bStartDate.getTime();
}

/**
 * Filters classes based on the filter options.  Returns a SortedAcademyClasses object with the filtered classes.
 * @param classes - The classes available to filter on
 * @param filterOptions - The filter options (days of week, time slots, class ids)
 *                        - if no array is provided for the given option, no filtering will be done on that option
 *                        - note that no array being provided is different from an empty array being provided
 * @returns An array of the filtered classes
 */
export function filterClasses(
	classes: AbbreviatedLegacyClass[],
	{classDays, courseIds, seasons, subjects}: FilterOptions = {}
): AbbreviatedLegacyClass[] {
	// Convert arrays to Sets for O(1) lookups
	const classDaysSet = classDays ? new Set(classDays) : null;
	const courseIdsSet = courseIds ? new Set(courseIds) : null;
	const seasonsSet = seasons ? new Set(seasons) : null;
	const subjectsSet = subjects ? new Set(subjects) : null;

	return classes.filter((classItem) => {
		if (
			seasonsSet &&
			seasonsSet.size > 0 &&
			!seasonsSet.has(classItem.seasonId)
		) {
			return false;
		}
		if (
			courseIdsSet &&
			courseIdsSet.size > 0 &&
			!courseIdsSet.has(classItem.courseId)
		) {
			return false;
		}
		if (
			classDaysSet &&
			classDaysSet.size > 0 &&
			!classDaysSet.has(makeClassDaysId(classItem))
		) {
			return false;
		}
		if (
			subjectsSet &&
			subjectsSet.size > 0 &&
			classItem.subject &&
			!subjectsSet.has(classItem.subject)
		) {
			return false;
		}
		return true;
	});
}

/**
 * Gets all the available seasons for a given set of classes in the shape:
 * {
 *   id: string;
 *   name: string;
 * }
 *
 * @param classes - The classes to get the seasons from
 * @returns An array of season options
 */
export function getAllSeasons(classes: AbbreviatedLegacyClass[]): {
	id: string;
	name: string;
}[] {
	const seasons = classes.reduce(
		(acc: {[key: string]: {id: string; name: string}}, classItem) => {
			const {seasonId} = classItem;
			if (seasonId in acc) {
				return acc;
			}
			const [year] = seasonId.split("-");
			let seasonName = seasonId;
			if (isAcademicYearSeason(seasonId)) {
				seasonName = `Academic Year ${year}-${parseInt(year) + 1}`;
			} else if (isSummerSeason(seasonId)) {
				seasonName = `Summer ${year}`;
			}
			acc[seasonId] = {
				id: seasonId,
				name: seasonName,
			};
			return acc;
		},
		{}
	);

	return Object.values(seasons).sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Gets all the available subjects for a given set of classes in the shape:
 * {
 *   id: string;
 *   name: string;
 * }
 *
 * The id and name are both the subject key.
 * The shape here is used to populate a checkbox group, but may be helpful elsewhere.
 *
 * @param academyClasses - The classes to get the subjects from
 * @returns An array of the time slots
 */
export function getAllSubjects(
	classes: AbbreviatedLegacyClass[]
): {id: string; name: string}[] {
	const subjects = classes.reduce(
		(acc: {[key: string]: {id: string; name: string}}, classItem) => {
			const subject = classItem.subject;
			if (!subject || subject in acc) {
				return acc;
			}
			acc[subject] = {
				id: subject,
				name: subject.replace(/_/g, " "),
			};
			return acc;
		},
		{}
	);

	return Object.values(subjects);
}

/**
 * Gets all the available course ids for a given set of classes in the shape:
 * {
 *   id: string;
 *   name: string;
 * }
 *
 * The id is the course id and the name is the course name.
 * The shape here is used to populate a checkbox group, but may be helpful elsewhere.
 *
 * @param academyClasses - The classes to get the course ids from
 * @returns An array of the course ids
 */
export function getAllCourseIds(
	classes: AbbreviatedLegacyClass[]
): {id: string; name: string}[] {
	const courseIds = classes.reduce(
		(acc: {[key: string]: {id: string; name: string}}, classItem) => {
			const {courseId, name} = classItem;
			if (courseId in acc) {
				return acc;
			}
			acc[courseId] = {id: courseId, name};
			return acc;
		},
		{}
	);

	return Object.values(courseIds).sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Gets all the available class days options for a given set of classes in the shape:
 * {
 *   id: string;
 *   name: string;
 * }
 *
 * @param classes - The classes to get the class days from
 * @returns An array of the class days
 */
function getAllClassDays(classes: AbbreviatedLegacyClass[]): {
	id: string;
	name: string;
}[] {
	const classDays = new Set<string>();
	classes.forEach((classItem) => {
		classDays.add(makeClassDaysId(classItem));
	});
	const classDaysOptions = Array.from(classDays).map((day) => ({
		id: day,
		name: makeClassDaysStringFromId(day),
	}));
	return classDaysOptions.sort((a, b) => {
		return compareClassDays(a.id, b.id);
	});
}

/**
 * Gets all the available categories for a given set of classes in the shape:
 * {
 *   daysOfWeek: {id: string; name: string}[];
 *   courseIds: {id: string; name: string}[];
 *   subjects: {id: string; name: string}[];
 * }
 *
 * @param academyClasses - The classes to get the categories from
 * @returns An object containing all the filterable categories
 */
export function getAllCategories(
	classes: AbbreviatedLegacyClass[]
): FilterSelectOptions {
	return {
		classDays: getAllClassDays(classes),
		courseIds: getAllCourseIds(classes),
		subjects: getAllSubjects(classes),
	};
}

/**
 * Parses URL parameters that support array syntax (param=value&param=value)
 */
export function getUrlParams(searchParams: URLSearchParams, paramName: string) {
	return searchParams.getAll(paramName);
}

/**
 * Updates the URL with the given param name and values.
 * If the param name is "season", the other params will be cleared. This is because some filters are mutually exclusive to the season.
 * If no values are provided, the param will be removed from the URL.
 */
export function updateUrlWithSearchParams(
	paramName: string,
	values: string[],
	urlConfigs: {
		searchParams: URLSearchParams;
		router: {replace: (url: string, options?: {scroll: boolean}) => void};
		pathname: string;
	},
	clearOtherParams = false
) {
	const {searchParams, router, pathname} = urlConfigs;
	const newSearchParams = new URLSearchParams(searchParams.toString());

	if (clearOtherParams) {
		// Get all existing param names and delete them
		const existingParams = Array.from(searchParams.keys());
		existingParams.forEach((param: string) => {
			if (param !== paramName) {
				newSearchParams.delete(param);
			}
		});
	}

	// Remove any existing values for this parameter
	newSearchParams.delete(paramName);

	// Only update if we have values, otherwise the param is already removed
	if (values.length > 0) {
		// Use proper array syntax by appending each value
		values.forEach((value) => {
			newSearchParams.append(paramName, value);
		});
	}

	router.replace(`${pathname}?${newSearchParams.toString()}`, {
		scroll: false,
	});
}
